import { CHANNELS } from "../../shared/channels";
import { hsl, toHex } from "../../shared/color";
import {
  ALIGNS, BORDERS, CONTRASTS, CORNERS, CURSORS, DEFAULT_LOOK, DENSITIES, DOCKS, ENTERS, FILTERS, FOCUSES, FONTS, GLASS, LOOK_PRESETS, MAX_SAVED_LOOKS, PANELS, RAILS, SLIDERS, SPACINGS, WIDTHS,
  Choice, LookPrefs, LookSnapshot, SavedLook, SliderInfo, cleanLook, cleanSavedLooks, decodeLook, encodeLook, presetSnapshot, safeThemeId, wearSnapshot
} from "../../shared/look_prefs";
import { PATTERNS, THEMES, themeById, DEFAULT_THEME_ID } from "../../shared/themes";
import { UserSettings } from "../../shared/types";
import { call } from "../bridge";
import { byId } from "../dom";
import { applyLook, currentLook } from "../appearance";
import { SFX_CHOICES, TAP_CHOICES, readLook, writeLook } from "../look";
import { activeThemeId, paintPattern, paintTheme, rememberChosenTheme } from "../theme";
import { rememberSettings } from "../settings_cache";
import { t } from "../i18n";

// the look studio in setup. six tabs, a live preview that stays in view, and every change paints at once and saves
// as you, so nothing here needs a save button. each control says what it does and when it has nothing to act on

const ACCENTS = ["#4de8ff", "#ff94d1", "#ff8a3d", "#c6ff00", "#ffb000", "#ff3340", "#a99bff", "#3dff7f"];
const BACKGROUNDS = ["#06080d", "#0c0907", "#140b1f", "#07120c", "#0c3b6e", "#1a1a1a", "#cfd4dc", "#f3d9e3", "#ece2cc"];

interface TabInfo {
  id: string;
  label: string;
  // what resetting this tab puts back
  keys: Array<keyof LookPrefs>;
}

const TABS: TabInfo[] = [
  { id: "looks", label: "looks", keys: [] },
  { id: "colour", label: "colour", keys: ["accent", "bg", "autoOn", "autoLight", "autoDark"] },
  { id: "shape", label: "shape and type", keys: ["corners", "font", "glass", "border", "caps"] },
  { id: "background", label: "background", keys: ["grain", "fog", "pattern", "vignette", "worlds"] },
  { id: "screen", label: "panels and screen", keys: ["panel", "filter", "filterStrength"] },
  { id: "cursor", label: "cursor", keys: ["cursor"] },
  { id: "layout", label: "layout", keys: ["width", "align", "density", "rail", "dock", "enter"] },
  { id: "access", label: "feel and access", keys: ["contrast", "focus", "spacing", "scale", "glitch"] }
];

// a mouse is the only thing that has a cursor, so touch devices never see the cursor tab
const hasMouse = (): boolean => matchMedia("(hover: hover) and (pointer: fine)").matches;
const tabList = (): TabInfo[] => TABS.filter((info) => info.id !== "cursor" || hasMouse());
// adult options show only while the switch is on
const offered = (list: Choice[], mature: boolean): Choice[] => list.filter((item) => mature || !item.mature);

export interface StudioHost {
  // the settings the studio last drew from, kept in step with the rest of setup
  changed(settings: UserSettings): void;
}

const TAB_KEY = "chix-look-tab";
let settings: UserSettings;
let host: StudioHost;
let saveTimer = 0;
let toastTimer = 0;
let tab = "looks";
let built = false;
// the long pickers rebuild with every pick, so where each was scrolled to is kept and put back
const pickScroll: Record<string, number> = {};

// ---------- small builders ----------

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

// a short message where the person is looking, not at the top of a long page
export function toast(text: string): void {
  let box = document.getElementById("look-toast");
  if (!box) {
    box = el("div", "look-toast");
    box.id = "look-toast";
    box.setAttribute("role", "status");
    box.setAttribute("aria-live", "polite");
    document.body.append(box);
  }
  box.textContent = t(text);
  // restart the transition so a second message shows again
  box.classList.remove("show");
  void box.offsetWidth;
  box.classList.add("show");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => box!.classList.remove("show"), 2200);
}

function group(key: string, title: string, intro?: string): HTMLElement {
  const wrap = el("div", "group");
  wrap.dataset.group = key;
  if (title) wrap.append(el("h3", "g-title", t(title)));
  if (intro) wrap.append(el("p", "g-hint", t(intro)));
  return wrap;
}

function choices(key: string, title: string, intro: string, list: Choice[], current: string, pick: (id: string) => void): HTMLElement {
  const wrap = group(key, title, intro);
  const seg = el("div", "choice-row");
  seg.setAttribute("role", "group");
  seg.setAttribute("aria-label", t(title));
  for (const item of list) {
    const button = el("button", "opt", t(item.label));
    button.type = "button";
    button.dataset.k = `${key}:${item.id}`;
    button.setAttribute("aria-pressed", String(item.id === current));
    if (item.hint) button.title = t(item.hint);
    button.addEventListener("click", () => pick(item.id));
    seg.append(button);
  }
  wrap.append(seg);
  // touch has no tooltips, so what the chosen option does is written out
  const chosen = list.find((item) => item.id === current);
  if (chosen?.hint) wrap.append(el("p", "g-hint", `${t(chosen.label)}: ${t(chosen.hint)}`));
  return wrap;
}

// a long list as a scroll-contained grid of cells under group headings. the face of each cell is up to the caller
function grouped(key: string, title: string, intro: string, list: Choice[], current: string, pick: (id: string) => void, face: (item: Choice) => HTMLElement): HTMLElement {
  const wrap = group(key, title, intro);
  const box = el("div", "pick-scroll");
  box.dataset.pk = key;
  box.setAttribute("role", "group");
  box.setAttribute("aria-label", t(title));
  box.tabIndex = -1;
  box.addEventListener("scroll", () => {
    pickScroll[key] = box.scrollTop;
  }, { passive: true });
  // group is optional on a choice, a list without any is one plain grid
  const sections: Array<{ name: string; items: Choice[] }> = [];
  for (const item of list) {
    const name = item.group || "";
    let at = sections.find((entry) => entry.name === name);
    if (!at) sections.push((at = { name, items: [] }));
    at.items.push(item);
  }
  for (const section of sections) {
    if (section.name) box.append(el("h4", "pick-head", t(section.name)));
    const grid = el("div", "pick-grid");
    for (const item of section.items) {
      const button = el("button", "pick-cell");
      button.type = "button";
      button.dataset.k = `${key}:${item.id}`;
      button.setAttribute("aria-pressed", String(item.id === current));
      button.setAttribute("aria-label", t(item.label));
      if (item.hint) button.title = t(item.hint);
      button.append(face(item), document.createTextNode(t(item.label)));
      button.addEventListener("click", () => pick(item.id));
      grid.append(button);
    }
    box.append(grid);
  }
  wrap.append(box);
  const chosen = list.find((item) => item.id === current);
  if (chosen?.hint) wrap.append(el("p", "g-hint", `${t(chosen.label)}: ${t(chosen.hint)}`));
  return wrap;
}

function putScrollBack(): void {
  for (const box of byId("look-root").querySelectorAll<HTMLElement>(".pick-scroll")) box.scrollTop = pickScroll[box.dataset.pk || ""] || 0;
}

function toggle(key: string, label: string, hint: string, value: boolean, pick: (on: boolean) => void): HTMLElement {
  const wrap = group(key, "");
  const row = el("label", "toggle");
  const input = el("input");
  input.type = "checkbox";
  input.checked = value;
  input.dataset.k = key;
  const track = el("span", "toggle-track");
  track.append(el("i"));
  input.addEventListener("change", () => pick(input.checked));
  row.append(input, track, document.createTextNode(t(label)));
  wrap.append(row, el("p", "g-hint", t(hint)));
  return wrap;
}

function swatches(key: string, colors: string[], current: string, pick: (hex: string, rebuild: boolean) => void): HTMLElement {
  const row = el("div", "color-row");
  row.setAttribute("role", "group");
  const none = el("button", "swatch-btn none");
  none.type = "button";
  none.dataset.k = `${key}:none`;
  none.title = t("the theme's own");
  none.setAttribute("aria-label", t("the theme's own"));
  none.setAttribute("aria-pressed", String(!current));
  none.addEventListener("click", () => pick("", true));
  row.append(none);
  for (const color of colors) {
    const button = el("button", "swatch-btn");
    button.type = "button";
    button.dataset.k = `${key}:${color}`;
    button.style.background = color;
    button.setAttribute("aria-label", color);
    button.setAttribute("aria-pressed", String(color === current));
    button.addEventListener("click", () => pick(color, true));
    row.append(button);
  }
  // the native picker fires on every drag, so it paints live and only rebuilds the studio once it is let go
  const custom = el("label", "color-pick");
  custom.title = t("pick any colour");
  const input = el("input");
  input.type = "color";
  input.value = current || "#4de8ff";
  input.dataset.k = `${key}:custom`;
  input.setAttribute("aria-label", t("pick any colour"));
  input.addEventListener("input", () => pick(input.value, false));
  input.addEventListener("change", () => pick(input.value, true));
  custom.append(input);
  row.append(custom);
  if (current && !colors.includes(current)) row.append(el("span", "color-name", current));
  return row;
}

function slider(item: SliderInfo, value: number, disabledWhy: string | null): HTMLElement {
  const row = el("div", "slider-row");
  row.classList.toggle("off", Boolean(disabledWhy));
  const id = `look-slider-${item.key}`;
  const title = el("label", "g-title", t(item.label));
  title.htmlFor = id;
  const out = el("output", "", `${value}%`);
  out.htmlFor = id;
  const undo = el("button", "undo", t("reset"));
  undo.type = "button";
  undo.disabled = value === DEFAULT_LOOK[item.key];
  undo.setAttribute("aria-label", `${t("reset")} ${t(item.label)}`);
  undo.addEventListener("click", () => change({ [item.key]: DEFAULT_LOOK[item.key] } as Partial<LookPrefs>));
  const input = el("input");
  input.type = "range";
  input.id = id;
  input.dataset.k = id;
  input.min = String(item.min);
  input.max = String(item.max);
  input.step = String(item.step);
  input.value = String(value);
  input.disabled = Boolean(disabledWhy);
  const fill = () => input.style.setProperty("--fill", `${((Number(input.value) - item.min) / (item.max - item.min)) * 100}%`);
  fill();
  input.addEventListener("input", () => {
    out.textContent = `${input.value}%`;
    undo.disabled = Number(input.value) === DEFAULT_LOOK[item.key];
    fill();
    change({ [item.key]: Number(input.value) } as Partial<LookPrefs>, false);
  });
  row.append(title, out, undo, input, el("p", "g-hint", t(disabledWhy || item.hint)));
  return row;
}

function notice(text: string, action?: { label: string; run: () => void }): HTMLElement {
  const box = el("div", "notice");
  box.setAttribute("role", "note");
  box.append(el("span", "", t(text)));
  if (action) {
    const button = el("button", "pill small", t(action.label));
    button.type = "button";
    button.addEventListener("click", action.run);
    box.append(button);
  }
  return box;
}

function themeSelect(value: string, label: string, pick: (id: string) => void): HTMLElement {
  const wrap = el("label", "", t(label));
  const select = el("select");
  const mature = cleanLook(settings.look).mature;
  select.replaceChildren(...THEMES.filter((theme) => mature || !theme.mature).map((theme) => Object.assign(el("option"), { value: theme.id, textContent: t(theme.label), selected: theme.id === value })));
  select.addEventListener("change", () => pick(select.value));
  wrap.append(select);
  return wrap;
}

// ---------- state ----------

function snapshot(): LookSnapshot {
  return { themeId: settings.themeId || DEFAULT_THEME_ID, patternId: settings.patternId || "none", look: cleanLook(settings.look) };
}

async function save(changes: Partial<UserSettings>): Promise<void> {
  settings = await call<UserSettings>(CHANNELS.settingsSave, changes);
  rememberSettings(settings);
  host.changed(settings);
}

// sliders fire constantly, so paint straight away and save once they settle
function change(changes: Partial<LookPrefs>, rerender = true): void {
  const look = cleanLook({ ...cleanLook(settings.look), ...changes });
  settings = { ...settings, look };
  applyLook(look);
  // following the device may have just switched on or changed its themes
  paintTheme(activeThemeId());
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => void save({ look }).catch(() => undefined), 350);
  if (rerender) render(settings);
}

async function apply(raw: LookSnapshot, message: string): Promise<void> {
  // a look from a preset, a saved slot or a pasted code is worn with this person's own mature switch, never its own
  const snap = wearSnapshot(raw, cleanLook(settings.look).mature);
  rememberChosenTheme(snap.themeId);
  settings = { ...settings, themeId: snap.themeId, patternId: snap.patternId, look: snap.look };
  applyLook(snap.look);
  paintTheme(activeThemeId());
  paintPattern(snap.patternId);
  render(settings);
  host.changed(settings);
  toast(message);
  await save({ themeId: snap.themeId, patternId: snap.patternId, look: snap.look }).catch(() => undefined);
}

function resetKeys(keys: Array<keyof LookPrefs>): void {
  const back: Record<string, unknown> = {};
  for (const key of keys) back[key] = DEFAULT_LOOK[key];
  change(back as Partial<LookPrefs>);
  toast("put back");
}

function randomSnapshot(): LookSnapshot {
  const rand = Math.random;
  const pickOf = <T,>(list: T[]): T => list[Math.floor(rand() * list.length)];
  const current = cleanLook(settings.look);
  const theme = pickOf(THEMES.filter((item) => current.mature || !item.mature));
  const accent = rand() < 0.6 ? toHex(hsl(rand() * 360, 0.75 + rand() * 0.2, theme.tone === "dark" ? 0.62 : 0.38)) : "";
  const look = cleanLook({
    ...current,
    accent,
    bg: "",
    corners: pickOf(CORNERS).id,
    font: pickOf(FONTS.filter((f) => f.id !== "dyslexia" || rand() < 0.2)).id,
    glass: pickOf(GLASS).id,
    border: pickOf(BORDERS).id,
    grain: Math.round(rand() * 20) * 10,
    fog: Math.round(rand() * 20) * 5,
    pattern: 60 + Math.round(rand() * 9) * 10,
    vignette: Math.round(rand() * 20) * 5,
    caps: rand() < 0.7,
    glitch: rand() < 0.6,
    enter: pickOf(ENTERS).id,
    panel: pickOf(PANELS).id,
    filter: rand() < 0.5 ? "none" : pickOf(FILTERS).id,
    filterStrength: 30 + Math.round(rand() * 10) * 5,
    // cursors only when there is a mouse to show them
    cursor: hasMouse() && rand() < 0.5 ? pickOf(offered(CURSORS, current.mature)).id : current.cursor
  });
  return { themeId: theme.id, patternId: rand() < 0.25 ? "none" : pickOf(PATTERNS).id, look };
}

// a preset counts as on when everything it sets matches
function isOn(snap: LookSnapshot, only?: Array<keyof LookPrefs>): boolean {
  const now = snapshot();
  if (snap.themeId !== now.themeId || snap.patternId !== now.patternId) return false;
  // the mature switch belongs to the person, not to a look
  const keys = only ?? (Object.keys(DEFAULT_LOOK) as Array<keyof LookPrefs>).filter((key) => key !== "mature");
  return keys.every((key) => snap.look[key] === now.look[key]);
}

// ---------- tabs ----------

function presetFace(snap: LookSnapshot): HTMLElement {
  const theme = themeById(snap.themeId);
  const face = el("span", "pc-face");
  const font = FONTS.find((item) => item.id === snap.look.font);
  const radius = snap.look.corners === "sharp" ? "0" : snap.look.corners === "soft" ? "7px" : snap.look.corners === "round" ? "16px" : "0 12px 0 0";
  face.style.setProperty("--pc-bg", snap.look.bg || theme.preview.case);
  face.style.setProperty("--pc-ink", theme.preview.ink === theme.preview.case ? theme.preview.tube : theme.preview.ink);
  face.style.setProperty("--pc-acc", snap.look.accent || theme.preview.tube);
  face.style.setProperty("--pc-r", radius);
  face.style.setProperty("--pc-font", font?.display || "var(--font-display)");
  face.append(el("b", "", "Aa"), el("i"));
  face.setAttribute("aria-hidden", "true");
  return face;
}

function presetCard(id: string, name: string, blurb: string, snap: LookSnapshot, only?: Array<keyof LookPrefs>, onRemove?: () => void): HTMLElement {
  const cell = el("div", "preset-cell");
  const on = isOn(snap, only);
  const button = el("button", "preset-card");
  button.type = "button";
  button.dataset.k = `preset:${id}`;
  button.setAttribute("aria-pressed", String(on));
  button.append(presetFace(snap), el("span", "pc-name", name));
  if (blurb) button.append(el("span", "pc-blurb", blurb));
  button.addEventListener("click", () => void apply(snap, `${name}: ${t("on")}`));
  cell.append(button);
  if (on) cell.append(el("span", "pc-on", t("on")));
  if (onRemove) {
    const x = el("button", "x", "×");
    x.type = "button";
    x.setAttribute("aria-label", `${t("delete this look")}: ${name}`);
    x.addEventListener("click", onRemove);
    cell.append(x);
  }
  return cell;
}

function tabLooks(): HTMLElement[] {
  const presets = el("section", "panel studio-box");
  presets.append(el("h2", "", t("presets")), el("p", "hint", t("one tap sets the theme, pattern and the look below. yours only, it saves as you")));
  const grid = el("div", "preset-grid");
  const mature = cleanLook(settings.look).mature;
  grid.replaceChildren(...LOOK_PRESETS.filter((preset) => mature || !preset.mature).map((preset) => {
    const snap = presetSnapshot(preset);
    return presetCard(preset.id, t(preset.label), t(preset.blurb), snap, Object.keys(preset.look) as Array<keyof LookPrefs>);
  }));
  presets.append(grid);
  const tools = el("div", "studio-row");
  const dice = el("button", "pill", t("surprise me"));
  dice.type = "button";
  dice.dataset.k = "dice";
  dice.addEventListener("click", () => void apply(randomSnapshot(), "rolled a new look"));
  const reset = el("button", "pill danger", t("reset everything"));
  reset.type = "button";
  reset.dataset.k = "reset-all";
  reset.addEventListener("click", () => {
    if (confirm(t("put the whole look back to chi signal?"))) void apply(presetSnapshot(LOOK_PRESETS[0]), "back to chi signal");
  });
  tools.append(dice, reset);
  presets.append(tools);

  const mine = el("section", "panel studio-box");
  mine.append(el("h2", "", t("your looks")));
  const saved = cleanSavedLooks(settings.savedLooks);
  if (saved.length) {
    const list = el("div", "preset-grid");
    list.replaceChildren(...saved.map((item) => presetCard(item.id, item.name, "", item, undefined, () => {
      if (confirm(t("delete this look?"))) void removeSaved(item.id);
    })));
    mine.append(list);
  } else mine.append(el("p", "hint", t("nothing saved yet. set things up the way you like, name it and save it here")));
  const row = el("div", "studio-row");
  const name = el("input");
  name.placeholder = t("name this look");
  name.maxLength = 40;
  name.dataset.k = "save-name";
  name.setAttribute("aria-label", t("name this look"));
  name.style.cssText = "flex:1 1 180px";
  const saveButton = el("button", "pill accent", t("save current look"));
  saveButton.type = "button";
  saveButton.addEventListener("click", () => void addSaved(name.value));
  name.addEventListener("keydown", (event) => {
    if (event.key === "Enter") void addSaved(name.value);
  });
  row.append(name, saveButton);
  mine.append(row);

  const share = el("section", "panel studio-box");
  share.append(el("h2", "", t("share a look")), el("p", "hint", t("copy your code and send it to anyone. paste theirs here and press use to wear it")));
  const area = el("textarea", "code-box");
  area.value = encodeLook(snapshot());
  area.spellcheck = false;
  area.dataset.k = "code";
  area.setAttribute("aria-label", t("look code"));
  const buttons = el("div", "studio-row");
  const copy = el("button", "pill", t("copy my code"));
  copy.type = "button";
  copy.addEventListener("click", async () => {
    area.value = encodeLook(snapshot());
    try {
      await navigator.clipboard.writeText(area.value);
      toast("code copied");
    } catch {
      area.select();
      toast("code selected, copy it by hand");
    }
  });
  const use = el("button", "pill accent", t("use this code"));
  use.type = "button";
  use.addEventListener("click", () => {
    const snap = decodeLook(area.value);
    if (!snap) return toast("that is not a look code");
    void apply(snap, "look on");
  });
  buttons.append(copy, use);
  share.append(area, buttons);
  return [presets, mine, share];
}

async function addSaved(rawName: string): Promise<void> {
  const list = cleanSavedLooks(settings.savedLooks);
  if (list.length >= MAX_SAVED_LOOKS) return toast("that is the most looks you can keep, delete one first");
  const entry: SavedLook = { id: Math.random().toString(36).slice(2, 10), name: rawName.trim() || t("my look"), ...snapshot() };
  settings = { ...settings, savedLooks: [...list, entry] };
  render(settings);
  toast("look saved");
  await save({ savedLooks: settings.savedLooks }).catch(() => undefined);
}

async function removeSaved(id: string): Promise<void> {
  settings = { ...settings, savedLooks: cleanSavedLooks(settings.savedLooks).filter((item) => item.id !== id) };
  render(settings);
  toast("look deleted");
  await save({ savedLooks: settings.savedLooks }).catch(() => undefined);
}

function tabColour(look: LookPrefs): HTMLElement {
  const box = el("section", "panel studio-box");
  box.append(el("h2", "", t("your colours")));
  const accent = group("accent", "accent", "the colour of highlights, selected buttons and the glow. it sits on top of any theme");
  accent.append(swatches("accent", ACCENTS, look.accent, (hex, rebuild) => change({ accent: hex }, rebuild)));
  const bg = group("bg", "background colour", "your own screen colour instead of the theme's. text and contrast follow on their own");
  bg.append(swatches("bg", BACKGROUNDS, look.bg, (hex, rebuild) => change({ bg: hex }, rebuild)));
  box.append(accent, bg);
  const auto = group("auto", "day and night", "switch themes by itself when this device goes light or dark");
  auto.append(toggle("autoOn", "follow the device", "off keeps the theme you pick above all day", look.autoOn, (on) => change({ autoOn: on })));
  if (look.autoOn) {
    const grid = el("div", "auto-grid");
    grid.append(themeSelect(look.autoLight, "in light mode", (id) => change({ autoLight: id }, false)), themeSelect(look.autoDark, "in dark mode", (id) => change({ autoDark: id }, false)));
    auto.append(grid);
  }
  box.append(auto, resetRow("colour"));
  return box;
}

function tabShape(look: LookPrefs): HTMLElement {
  const box = el("section", "panel studio-box");
  box.append(el("h2", "", t("shape and type")));
  box.append(
    choices("corners", "corners", "how panels, buttons and cards are shaped", CORNERS, look.corners, (id) => change({ corners: id })),
    grouped("font", "font", "the typeface for titles, labels and text. every one is bundled, nothing is downloaded from outside", FONTS, look.font, (id) => change({ font: id }), (item) => {
      const face = el("span", "font-face", "Aa");
      face.style.fontFamily = (item as { display?: string }).display || "var(--font-display)";
      return face;
    }),
    choices("glass", "panel glass", "how see-through panels are over the background", GLASS, look.glass, (id) => change({ glass: id })),
    choices("border", "outlines", "how strong the thin lines around things are", BORDERS, look.border, (id) => change({ border: id })),
    toggle("caps", "capital letters on labels", "off writes labels in normal lower case, easier to read", look.caps, (on) => change({ caps: on }, false)),
    resetRow("shape")
  );
  return box;
}

function tabBackground(look: LookPrefs): HTMLElement[] {
  const slidersBox = el("section", "panel studio-box");
  slidersBox.append(el("h2", "", t("background effects")));
  const device = readLook();
  const noPattern = (settings.patternId || "none") === "none";
  if (!device.scanlines) {
    slidersBox.append(notice("film grain and fog are switched off on this device, so those two sliders have nothing to move", {
      label: "turn them on",
      run: () => {
        writeLook({ scanlines: true });
        render(settings);
        toast("grain and fog on");
      }
    }));
  }
  for (const item of SLIDERS.filter((entry) => ["grain", "fog", "pattern", "vignette"].includes(entry.key))) {
    let why: string | null = null;
    if (!device.scanlines && (item.key === "grain" || item.key === "fog")) why = "off on this device, turn it on above";
    if (item.key === "pattern" && noPattern) why = "pick a pattern above and this will change how strong it is";
    slidersBox.append(slider(item, look[item.key], why));
  }
  slidersBox.append(toggle("worlds", "station worlds", "each station gets its own colour, moving backdrop and a title that locks in when a page opens", look.worlds, (on) => change({ worlds: on }, false)));
  slidersBox.append(toggle("device-grain", "grain and fog on this device", "this switch is for this browser only. use it to calm a busy screen or a slow device", device.scanlines, (on) => {
    writeLook({ scanlines: on });
    render(settings);
  }), resetRow("background"));
  return [slidersBox];
}

function tabScreen(look: LookPrefs): HTMLElement[] {
  const frames = el("section", "panel studio-box");
  frames.append(el("h2", "", t("panel style")));
  frames.append(
    grouped("panel", "panel style", "how cards, panels and tiles are framed. each swatch is drawn with the style itself", PANELS, look.panel, (id) => change({ panel: id }), (item) => {
      const swatch = el("span", "pn-swatch");
      swatch.dataset.pn = item.id;
      return swatch;
    }),
    resetRow("screen")
  );
  const filter = el("section", "panel studio-box");
  filter.append(el("h2", "", t("screen filter")));
  filter.append(
    choices("filter", "filter", "a layer over the whole screen. it never blocks a tap and stays still with calm motion", FILTERS, look.filter, (id) => change({ filter: id })),
    slider(SLIDERS.find((item) => item.key === "filterStrength")!, look.filterStrength, look.filter === "none" ? "pick a filter above and this will change how strong it is" : null)
  );
  return [frames, filter];
}

function tabCursor(look: LookPrefs): HTMLElement {
  const box = el("section", "panel studio-box");
  box.append(el("h2", "", t("mouse cursor")));
  const list = offered(CURSORS, look.mature);
  box.append(
    grouped("cursor", "cursor pack", "swaps the arrow and the hand for a themed pair. it only shows with a mouse, and each cell shows both: the arrow, then the hand over things you can press", list, look.cursor, (id) => change({ cursor: id }), (item) => {
      const pair = el("span", "pick-pair");
      if (item.id === "default") {
        pair.append(el("span", "pn-none", "\u2196"));
        return pair;
      }
      for (const suffix of ["", "-ptr"]) {
        const img = el("img");
        img.src = `assets/cursors/${item.id}${suffix}.svg`;
        img.alt = "";
        img.width = 32;
        img.height = 32;
        img.loading = "lazy";
        pair.append(img);
      }
      return pair;
    }),
    resetRow("cursor")
  );
  return box;
}

function tabLayout(look: LookPrefs): HTMLElement {
  const box = el("section", "panel studio-box");
  box.append(el("h2", "", t("layout")));
  box.append(
    choices("width", "page width", "how wide the page content gets. it only shows on screens wider than the setting, so on a phone nothing changes", WIDTHS, look.width, (id) => change({ width: id })),
    choices("align", "page position", "where the content sits on wide screens", ALIGNS, look.align, (id) => change({ align: id })),
    choices("density", "spacing", "how close or far apart things sit", DENSITIES, look.density, (id) => change({ density: id })),
    choices("rail", "menu side, tablets and computers", "which side the menu with the dial sits on, on screens wider than 900 px", RAILS, look.rail, (id) => change({ rail: id })),
    choices("dock", "dock position, phones", "where the radio dock sits on phones and portrait tablets", DOCKS, look.dock, (id) => change({ dock: id })),
    choices("enter", "when a page opens", "the motion as you switch pages", ENTERS, look.enter, (id) => change({ enter: id })),
    resetRow("layout")
  );
  return box;
}

function tabAccess(look: LookPrefs): HTMLElement[] {
  const comfort = el("section", "panel studio-box");
  comfort.append(el("h2", "", t("size and comfort")));
  comfort.append(
    slider(SLIDERS.find((item) => item.key === "scale")!, look.scale, null),
    choices("contrast", "contrast", "brighter text and stronger lines", CONTRASTS, look.contrast, (id) => change({ contrast: id })),
    choices("focus", "keyboard focus ring", "how clearly the selected control is marked when you use the keyboard", FOCUSES, look.focus, (id) => change({ focus: id })),
    choices("spacing", "text spacing", "more room between letters, words and lines, kinder on tired eyes", SPACINGS, look.spacing, (id) => change({ spacing: id })),
    toggle("glitch", "glitch on page titles", "turn off the quick flicker when a page opens", look.glitch, (on) => change({ glitch: on }, false)),
    toggle("mature", "adult themes and cursors", "Adult themes and cursors, suggestive but never explicit. Off by default. Only affects your own screen.", look.mature, (on) => void setMature(on)),
    toggle("calm", "calm motion", "stops the fog, falling pieces and most movement on this device", readLook().calm, (on) => {
      writeLook({ calm: on });
      render(settings);
    }),
    resetRow("access")
  );

  const device = readLook();
  const feel = el("section", "panel studio-box");
  feel.append(el("h2", "", t("this device only")));
  feel.append(
    choices("sfx", "button sounds", "a short beep when you press a button, like a console menu", SFX_CHOICES.map((id) => ({ id, label: id })), device.sfx, (id) => {
      writeLook({ sfx: id });
      render(settings);
      toast(id === "off" ? "sounds off" : "sounds on");
    }),
    choices("tap", "tap effect", "what appears where you touch the screen", TAP_CHOICES.map((id) => ({ id, label: id })), device.tap, (id) => {
      writeLook({ tap: id });
      render(settings);
      toast(id === "off" ? "tap effect off" : "tap effect on");
    }),
    toggle("juice", "sparkle effects", "particle bursts when you finish things, a soft glow that follows the mouse and press pulses", device.juice, (on) => writeLook({ juice: on })),
    toggle("haptics", "vibration", "a short buzz on phones when you finish a task or unlock something", device.haptics, (on) => writeLook({ haptics: on })),
    toggle("tilt", "tilt and magnetic", "tiles lean toward your finger or pointer and main buttons pull toward the cursor", device.tilt, (on) => writeLook({ tilt: on }))
  );
  return [comfort, feel];
}

// turning the switch off puts back anything adult that was on, so nothing stays active that the pickers no longer show
async function setMature(on: boolean): Promise<void> {
  const look = cleanLook({ ...cleanLook(settings.look), mature: on });
  const changes: Partial<UserSettings> = { look };
  const themeId = safeThemeId(settings.themeId || DEFAULT_THEME_ID, on);
  if (themeId !== (settings.themeId || DEFAULT_THEME_ID)) {
    changes.themeId = themeId;
    rememberChosenTheme(themeId);
  }
  settings = { ...settings, ...changes };
  applyLook(look);
  paintTheme(activeThemeId());
  render(settings);
  host.changed(settings);
  toast(on ? "adult options on" : "adult options off");
  await save(changes).catch(() => undefined);
}

function resetRow(tabId: string): HTMLElement {
  const info = TABS.find((item) => item.id === tabId)!;
  const row = el("div", "studio-row");
  const button = el("button", "opt", t("put this page back"));
  button.type = "button";
  button.dataset.k = `reset:${tabId}`;
  button.addEventListener("click", () => resetKeys(info.keys));
  row.append(button);
  return row;
}

// ---------- shell ----------

function buildShell(): void {
  const rootNode = byId("look-root");
  rootNode.replaceChildren();
  rootNode.classList.add("studio");

  const preview = el("div", "look-preview");
  preview.setAttribute("aria-hidden", "true");
  const rail = el("div", "pv-rail");
  rail.append(el("i"), el("i"), el("i"), el("i"));
  const main = el("div", "pv-main");
  const page = el("div", "pv-page");
  const card = el("div", "panel pv-card");
  const row = el("div", "pv-row");
  row.append(el("span", "pill small accent", t("button")), el("span", "chip active", t("chip")));
  card.append(el("h2", "", t("preview")), el("p", "", t("the quick brown fox jumps over the lazy dog")), row);
  page.append(el("h1", "", t("Setup")), card);
  const dock = el("div", "pv-dock");
  dock.append(el("i"), el("i"), el("i"));
  main.append(page, dock);
  preview.append(rail, main);

  const tabs = el("div", "look-tabs");
  tabs.setAttribute("role", "tablist");
  tabs.setAttribute("aria-label", t("look settings"));
  for (const info of tabList()) {
    const button = el("button", "look-tab", t(info.label));
    button.type = "button";
    button.id = `look-tab-${info.id}`;
    button.setAttribute("role", "tab");
    button.setAttribute("aria-controls", `look-panel-${info.id}`);
    button.addEventListener("click", () => showTab(info.id));
    button.addEventListener("keydown", (event) => {
      const shown = tabList();
      const at = shown.findIndex((item) => item.id === tab);
      const next = event.key === "ArrowRight" ? at + 1 : event.key === "ArrowLeft" ? at - 1 : event.key === "Home" ? 0 : event.key === "End" ? shown.length - 1 : -1;
      if (next < 0) return;
      event.preventDefault();
      showTab(shown[(next + shown.length) % shown.length].id);
      byId(`look-tab-${tab}`).focus();
    });
    tabs.append(button);
  }

  const panels = tabList().map((info) => {
    const panel = el("div", "look-panel");
    panel.id = `look-panel-${info.id}`;
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", `look-tab-${info.id}`);
    const top = el("div", "look-slot-top");
    const dynamic = el("div", "look-slot");
    dynamic.id = `look-slot-${info.id}`;
    // the theme and pattern pickers are filled by setup and live in a fixed spot so a rebuild never empties them
    if (info.id === "colour") {
      const themes = el("section", "panel studio-box");
      themes.append(el("h2", "", t("themes")), el("p", "hint", t("pick a look")));
      const board = el("div", "theme-board");
      board.id = "theme-board";
      themes.append(board);
      top.append(themes);
    }
    if (info.id === "background") {
      const patterns = el("section", "panel studio-box");
      patterns.append(el("h2", "", t("background pattern")), el("p", "hint", t("a faint pattern behind the pages. the animated ones stay still with calm motion")));
      const board = el("div", "pattern-board");
      board.id = "pattern-board";
      patterns.append(board);
      top.append(patterns);
    }
    panel.append(top, dynamic);
    return panel;
  });

  rootNode.append(preview, tabs, ...panels);
  built = true;
}

function showTab(id: string): void {
  tab = tabList().some((item) => item.id === id) ? id : "looks";
  try {
    localStorage.setItem(TAB_KEY, tab);
  } catch {
    // private mode
  }
  for (const info of tabList()) {
    const selected = info.id === tab;
    const button = byId(`look-tab-${info.id}`);
    button.setAttribute("aria-selected", String(selected));
    button.tabIndex = selected ? 0 : -1;
    byId(`look-panel-${info.id}`).hidden = !selected;
  }
  putScrollBack();
}

function render(next: UserSettings): void {
  settings = next;
  if (!built) buildShell();
  // a slider being dragged must not be rebuilt under the finger
  const active = document.activeElement;
  const inside = active instanceof HTMLElement && byId("look-root").contains(active);
  if (inside && active instanceof HTMLInputElement && active.type === "range") return;
  const focusKey = inside ? (active as HTMLElement).dataset.k : undefined;
  const look = cleanLook(settings.look);

  byId("look-slot-looks").replaceChildren(...tabLooks());
  byId("look-slot-colour").replaceChildren(tabColour(look));
  byId("look-slot-shape").replaceChildren(tabShape(look));
  byId("look-slot-background").replaceChildren(...tabBackground(look));
  byId("look-slot-screen").replaceChildren(...tabScreen(look));
  if (hasMouse()) byId("look-slot-cursor").replaceChildren(tabCursor(look));
  byId("look-slot-layout").replaceChildren(tabLayout(look));
  byId("look-slot-access").replaceChildren(...tabAccess(look));
  showTab(tab);
  putScrollBack();

  if (focusKey) byId("look-root").querySelector<HTMLElement>(`[data-k="${CSS.escape(focusKey)}"]`)?.focus({ preventScroll: true });
}

export function setupLookStudio(studioHost: StudioHost): { show(settings: UserSettings): void } {
  host = studioHost;
  try {
    tab = localStorage.getItem(TAB_KEY) || "looks";
  } catch {
    tab = "looks";
  }
  return {
    show(next) {
      settings = next;
      applyLook(cleanLook(next.look));
      render(next);
    }
  };
}

// handy for the theme cards: when following the device, picking a theme sets the slot for the mode it is in now
export function followSlotChange(themeId: string): Partial<LookPrefs> | null {
  const look = currentLook();
  if (!look.autoOn) return null;
  return matchMedia("(prefers-color-scheme: dark)").matches ? { autoDark: themeId } : { autoLight: themeId };
}
